import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { services } from '@/data/services';
import { useLanguage } from '@/hooks/useLanguage';
import { getServiceContent, getContent } from '@/utils/contentLoader';
import { useRTLArrow } from '@/hooks/useRTL';

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { language } = useLanguage();
  const { getArrow } = useRTLArrow();
  const BackArrow = getArrow('left');

  useEffect(() => {
    document.title = 'Service Details - ElhamyLabs';
    // Scroll to top when component mounts or serviceId changes
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [serviceId]);

  // Map route to service
  const service = services.find((s) => s.route === `/services/${serviceId}`);
  
  // Get service-specific content from content files
  const serviceContent = getServiceContent(serviceId || '', language);
  const content = getContent(language);
  const servicesContent = content.services;

  if (!service || !serviceContent) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-dark-text">Service Not Found</h1>
          <Link to="/services" className="text-brand-purple-base dark:text-brand-orange-base hover:underline">
            {servicesContent?.backToServices || 'Back to Services'}
          </Link>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 mb-8 text-white/80 hover:text-white transition-colors"
          >
            <BackArrow size={20} />
            {servicesContent.backToServices}
          </Link>
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {serviceContent.name}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              {serviceContent.heroDescription}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ScrollReveal direction="right">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-light-text dark:text-dark-text">
                  {serviceContent.aboutTitle}
                </h2>
                <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary mb-6">
                  {serviceContent.aboutDescription}
                </p>
                <p className="text-lg text-light-text-secondary dark:text-dark-text-secondary">
                  {serviceContent.aboutDescription2}
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left">
              <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl">
                <h2 className="text-3xl font-bold mb-6 text-light-text dark:text-dark-text">
                  {serviceContent.keyFeatures.title}
                </h2>
                <ul className="space-y-4">
                  {serviceContent.keyFeatures.items.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle size={24} className="text-brand-orange-base flex-shrink-0 mt-1" />
                      <span className="text-light-text-secondary dark:text-dark-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/home-visit-request"
                  className="mt-8 w-full inline-flex justify-center px-6 py-3 bg-brand-orange-base text-white rounded-lg hover:bg-brand-orange-light transition-colors font-semibold"
                >
                  {content.common.book}
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;

